<template>
  <div class="multi-file-upload">
    <!-- 文件列表展示区域 -->
    <div class="file-list" v-if="files && files.length > 0">
      <div
        v-for="(item, index) in files"
        :key="index"
        class="file-item"
      >
        <div class="file-info" :title="item.fileName">
          <i class="el-icon-document file-icon"></i>
          <span class="file-name">{{ item.fileName }}</span>
        </div>
        <div class="file-actions">
          <el-button
            type="text"
            size="mini"
            @click="handlePreview(item)"
            v-if="item.filePath"
            >查看</el-button
          >
          <el-button
            type="text"
            size="mini"
            class="delete-btn"
            @click="handleDelete(index)"
            v-if="!disabled"
            >删除</el-button
          >
        </div>
      </div>
    </div>

    <!-- 禁用/查看模式下且无文件时的说明 -->
    <div v-else-if="disabled" class="empty-file-tip">暂无文件</div>

    <!-- 上传按钮触发区域 -->
    <el-upload
      v-if="!disabled"
      class="upload-btn-wrapper"
      action=""
      :show-file-list="false"
      :before-upload="beforeUpload"
      :http-request="uploadRequest"
      :on-success="handleSuccess"
      :accept="accept"
      :multiple="true"
    >
      <el-button size="small" type="primary" icon="el-icon-upload2" :loading="loading"
        >上传文件</el-button
      >
    </el-upload>
  </div>
</template>

<script>
import { toolsts } from "@/api";
import { guid } from "@/utils/other.js";
import ObsClient from "esdk-obs-browserjs";

export default {
  name: "FormMultiFileUpload",
  props: {
    /**
     * 文件列表数组，格式: [{ fileName: '文件名', filePath: '文件URL' }]
     */
    files: {
      type: Array,
      default: () => [],
    },
    /**
     * 是否禁用上传与删除功能
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 允许上传的单文件最大尺寸（MB）
     */
    maxSize: {
      type: Number,
      default: 500,
    },
    /**
     * 限制上传的文件扩展名类型
     */
    accept: {
      type: String,
      default: ".pdf",
    },
  },
  data() {
    return {
      /**
       * 文件上传加载状态标志
       */
      loading: false,
    };
  },
  methods: {
    /**
     * 文件上传前的校验钩子函数
     * @param {Object} file - 准备上传的文件对象
     * @returns {boolean} 是否允许继续上传
     */
    beforeUpload(file) {
      const isLt = file.size / 1024 / 1024 < this.maxSize;
      const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

      if (this.accept) {
        const acceptArr = this.accept.split(",").map((s) => s.trim().toLowerCase());
        if (!acceptArr.includes(ext)) {
          this.$message.error(`文件格式不正确，仅支持上传 ${this.accept} 格式的文件`);
          return false;
        }
      }

      if (!isLt) {
        this.$message.error(`上传文件大小不能超过 ${this.maxSize}MB!`);
        return false;
      }
      this.loading = true;
      return true;
    },

    /**
     * 自定义文件上传请求函数，接入华为云 OBS 存储
     * @param {Object} option - el-upload 传入的配置与回调参数对象
     * @returns {Promise<void>}
     */
    async uploadRequest(option) {
      try {
        let pos = option.file.name.lastIndexOf(".");
        let extendName = option.file.name.substring(pos + 1);
        let fileNames = `backstage/${guid()}.${extendName}`;

        const res = await toolsts();
        if (res.code === 200) {
          const { bucket, endpoint, ak, sk } = res.data;
          const obsClient = new ObsClient({
            access_key_id: ak,
            secret_access_key: sk,
            server: `https://${endpoint}`,
            x_obs_acl: "public-read",
          });
          obsClient.putObject(
            {
              Bucket: bucket,
              Key: fileNames,
              Body: option.file,
              ACL: "public-read",
            },
            (err, result) => {
              if (err) {
                this.$message.error("文件上传失败");
                this.loading = false;
                option.onError(err);
              } else {
                if (result.CommonMsg.Status == 200) {
                  let filePath = `https://${bucket}.${endpoint}/${fileNames}`;
                  option.onSuccess({ filePath, fileName: option.file.name });
                  this.loading = false;
                } else {
                  this.$message.error("文件上传失败");
                  this.loading = false;
                  option.onError(new Error("Upload failed"));
                }
              }
            }
          );
        } else {
          this.$message.error(res.msg || "获取上传凭证失败");
          this.loading = false;
          option.onError(new Error(res.msg));
        }
      } catch (e) {
        this.$message.error("文件上传失败");
        this.loading = false;
        option.onError(e);
      }
    },

    /**
     * 文件上传成功后的响应处理
     * @param {Object} res - 上传成功包含 filePath 和 fileName 的数据对象
     * @returns {void}
     */
    handleSuccess(res) {
      if (res && res.filePath) {
        const newList = [
          ...this.files,
          { fileName: res.fileName, filePath: res.filePath },
        ];
        this.$emit("change", newList);
      }
    },

    /**
     * 移除指定索引的文件项
     * @param {number} index - 需移除的文件在数组中的索引位置
     * @returns {void}
     */
    handleDelete(index) {
      const newList = this.files.filter((_, i) => i !== index);
      this.$emit("change", newList);
    },

    /**
     * 查看/下载指定文件
     * @param {Object} item - 文件项对象 { fileName, filePath }
     * @returns {void}
     */
    handlePreview(item) {
      if (item.filePath) {
        window.open(item.filePath, "_blank");
      }
    },
  },
};
</script>

<style scoped>
.multi-file-upload {
  width: 400px;
}
.file-list {
  margin-bottom: 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 6px 12px;
  background-color: #fafafa;
}
.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}
.file-info {
  display: flex;
  align-items: center;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.file-icon {
  margin-right: 6px;
  color: #409eff;
}
.file-name {
  font-size: 13px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.file-actions {
  display: flex;
  align-items: center;
}
.delete-btn {
  color: #f56c6c;
  margin-left: 10px;
}
.upload-btn-wrapper {
  display: inline-block;
}
.empty-file-tip {
  color: #909399;
  font-size: 14px;
  line-height: 32px;
}
</style>
